/**
 * Splits text-layer markup into animatable units by wrapping runs of text in
 * `<span data-ht-unit="i">` spans, the shape the unit-stagger evaluator styles.
 *
 * Tags are never crossed: a word or line interrupted by an inline tag
 * (`He<b>llo</b>`) becomes two spans sharing one unit index, and the evaluator
 * styles every span carrying that index. Whitespace stays outside letter and
 * word spans so inline-block units keep their natural spacing.
 */
import type { TextUnit } from "./animation-blocks";

const HT_UNIT_ATTR = "data-ht-unit";

/** A tag, a character entity, a whitespace run, or a run of text. */
const TOKEN = /<[^>]*>|&[a-zA-Z0-9#]+;|\s+|[^<&\s]+/g;

/** Letters are user-perceived characters, so a flag or an accented letter
 * built from several code points stays one unit. */
const graphemes = new Intl.Segmenter(undefined, { granularity: "grapheme" });

/** One letter unit per grapheme of a text run; an entity is one unit. */
function letters(token: string): string[] {
  if (token.startsWith("&")) return [token];
  return Array.from(graphemes.segment(token), ({ segment }) => segment);
}

const LINE_BREAK_TAG = /^<(?:br\b|\/(?:p|div|li|h[1-6])\b)/i;

function openSpan(index: number, attribute: string): string {
  return `<span ${attribute}="${index}">`;
}

/**
 * Wraps `content` for the given unit. Returns the input unchanged when it
 * already carries unit spans or holds no text to wrap. `attribute` names the
 * span attribute; the renderer uses a per-unit name when one text nests
 * several unit types.
 */
export function wrapTextUnits(
  content: string,
  unit: TextUnit,
  attribute: typeof HT_UNIT_ATTR | `data-ht-unit-${TextUnit}` = HT_UNIT_ATTR,
): string {
  if (content.includes(`${attribute}=`)) return content;
  const tokens = content.match(TOKEN) ?? [];

  const out: string[] = [];
  let nextIndex = 0;
  // Index of the span currently open, or null between spans.
  let open: number | null = null;
  // For words and lines: the unit a following text token continues, so a tag
  // boundary inside a word or line does not start a new unit.
  let current: number | null = null;

  const close = () => {
    if (open !== null) {
      out.push("</span>");
      open = null;
    }
  };

  for (const token of tokens) {
    if (token.startsWith("<")) {
      close();
      if (unit !== "letter" && LINE_BREAK_TAG.test(token)) current = null;
      out.push(token);
      continue;
    }

    if (/^\s+$/.test(token)) {
      if (unit === "line" && current !== null && open !== null) {
        out.push(token);
        continue;
      }
      close();
      if (unit === "word") current = null;
      out.push(token);
      continue;
    }

    if (unit === "letter") {
      for (const letter of letters(token)) {
        out.push(openSpan(nextIndex++, attribute), letter, "</span>");
      }
      continue;
    }

    if (current === null) current = nextIndex++;
    if (open === null) {
      out.push(openSpan(current, attribute));
      open = current;
    }
    out.push(token);
  }
  close();

  return nextIndex === 0 ? content : out.join("");
}
